import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  LocalesFilterParams,
  LocaleType,
  LocalesListResponse,
  CreateLocaleParams,
  UpdateLocaleParams
} from '@/types/locale'
import { BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import {
  fetchLocales,
  fetchLocale,
  updateLocale,
  deleteLocale,
  createLocale,
  sortLocale,
  bulkDeleteLocales
} from '@/lib/api'
import { SortEnum, SortOrderEnum } from '@/enums'

// Single locale state type
type LocaleStateType = {
  isLoading: boolean
  response: LocaleType | null
  error: {
    status?: number
    message: string
  } | null
}

// Locales list state type
type LocalesListStateType = {
  isLoading: boolean
  response: LocalesListResponse | null
  error: {
    status?: number
    message: string
  } | null
}

type LocalesStoreState = {
  // Locales list filters state
  filters: LocalesFilterParams
  setFilters: (filters: Partial<LocalesFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  // Locales list state
  localesListState: LocalesListStateType
  fetchLocales: (params?: LocalesFilterParams) => Promise<LocalesListResponse>
  resetLocalesList: () => void

  // Sort locale
  sortLocale: (localeId: string, type: SortOrderEnum) => Promise<LocaleType>

  // Single locale state
  localeState: LocaleStateType
  getLocale: (localeId: string) => Promise<LocaleType>
  createLocale: (params: CreateLocaleParams) => Promise<LocaleType>
  updateLocale: (localeId: string, params: UpdateLocaleParams) => Promise<LocaleType>
  deleteLocale: (localeId: string) => Promise<void>
  resetLocale: () => void

  // Selection operations
  selectedLocaleIds: string[]
  selectLocale: (id: string) => void
  deselectLocale: (id: string) => void
  toggleLocaleSelection: (id: string) => void
  selectAllLocales: (locales: LocaleType[]) => void
  deselectAllLocales: () => void
  isLocaleSelected: (id: string) => boolean

  // Bulk delete operation
  bulkDeleteLocales: (ids: string[]) => Promise<BulkDeleteResponse>
}

const defaultFilters: LocalesFilterParams = {
  page: 1,
  size: 20,
  sort: SortEnum.ASC,
  sortBy: 'order'
}

const initialLocaleState: LocaleStateType = {
  isLoading: false,
  response: null,
  error: null
}

const initialLocalesListState: LocalesListStateType = {
  isLoading: false,
  response: null,
  error: null
}

const useLocalesStore = create<LocalesStoreState>()(
  devtools((set, get) => ({
    filters: defaultFilters,
    localeState: initialLocaleState,
    localesListState: initialLocalesListState,
    selectedLocaleIds: [],

    setFilters: newFilters =>
      set(state => {
        const updatedFilters = { ...state.filters }

        Object.entries(newFilters).forEach(([key, value]) => {
          if (value === undefined) {
            delete updatedFilters[key as keyof LocalesFilterParams]
          } else {
            updatedFilters[key as keyof LocalesFilterParams] = value as any
          }
        })

        return { filters: updatedFilters }
      }),

    resetFilters: () => set({ filters: defaultFilters }),

    updateUrlParams: searchParams => {
      const filters: LocalesFilterParams = {}

      searchParams.forEach((value, key) => {
        switch (key) {
          case 'page':
          case 'size':
            filters[key] = parseInt(value, 10)
            break
          case 'sort':
            filters[key] = value as SortEnum
            break
          case 'sortBy':
            filters[key] = value as LocalesFilterParams['sortBy']
            break
          case 'active':
            filters[key] = value === 'true'
            break
          case 'createdUsers':
          case 'updatedUsers':
            filters[key] = value
            break
          case 'direction':
            filters[key] = value as LocalesFilterParams['direction']
            break
          default:
            filters[key as keyof LocalesFilterParams] = value as any
            break
        }
      })

      set({ filters: { ...defaultFilters, ...filters } })
    },

    getUrlParams: () => {
      const { filters } = get()
      const params = new URLSearchParams()

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            if (value.length > 0) {
              params.set(key, value.join(','))
            }
          } else {
            params.set(key, value.toString())
          }
        }
      })

      return params
    },

    // Single locale operations
    getLocale: async (localeId: string) => {
      // If already loading, don't make another request
      const currentState = get().localeState
      if (currentState.isLoading) {
        return currentState.response!
      }

      set(() => ({
        localeState: {
          ...initialLocaleState,
          isLoading: true
        }
      }))

      try {
        const data = await fetchLocale(localeId)

        set(() => ({
          localeState: {
            ...initialLocaleState,
            response: data
          }
        }))
        return data
      } catch (error: any) {
        set(() => ({
          localeState: {
            ...initialLocaleState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    createLocale: async (params: CreateLocaleParams) => {
      set(() => ({
        localeState: {
          ...initialLocaleState,
          isLoading: true
        }
      }))

      try {
        const data = await createLocale(params)

        set(() => ({
          localeState: {
            ...initialLocaleState,
            response: data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          localeState: {
            ...initialLocaleState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    updateLocale: async (localeId: string, params: UpdateLocaleParams) => {
      set(() => ({
        localeState: {
          ...get().localeState,
          isLoading: true
        }
      }))

      try {
        const data = await updateLocale(localeId, params)

        set(() => ({
          localeState: {
            ...get().localeState,
            isLoading: false,
            response: data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          localeState: {
            ...initialLocaleState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    sortLocale: async (localeId: string, type: SortOrderEnum) => {
      set(() => ({
        localeState: {
          ...get().localeState,
          isLoading: true
        }
      }))

      try {
        const data = await sortLocale(localeId, type)

        set(() => ({
          localeState: {
            ...get().localeState,
            isLoading: false,
            response: data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          localeState: {
            ...initialLocaleState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    deleteLocale: async (localeId: string) => {
      set(() => ({
        localeState: {
          ...get().localeState,
          isLoading: true
        }
      }))

      try {
        await deleteLocale(localeId)

        set(() => ({
          localeState: initialLocaleState
        }))
      } catch (error: any) {
        set(() => ({
          localeState: {
            ...initialLocaleState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    resetLocale: () => {
      set(() => ({
        localeState: initialLocaleState
      }))
    },

    // Locales list operations
    fetchLocales: async (params?: LocalesFilterParams) => {
      // If already loading, don't make another request
      const currentState = get().localesListState
      if (currentState.isLoading) {
        return currentState.response!
      }

      set(() => ({
        localesListState: {
          ...initialLocalesListState,
          isLoading: true
        }
      }))

      try {
        // Use provided params or fall back to store filters
        const filtersToUse = params || get().filters
        const data = await fetchLocales(filtersToUse)

        set(() => ({
          localesListState: {
            ...initialLocalesListState,
            response: data
          }
        }))
        return data
      } catch (error: any) {
        set(() => ({
          localesListState: {
            ...initialLocalesListState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    resetLocalesList: () => {
      set(() => ({
        localesListState: initialLocalesListState
      }))
    },

    // Selection operations
    selectLocale: id => {
      set(state => {
        if (state.selectedLocaleIds.includes(id)) {
          return state
        }
        return { selectedLocaleIds: [...state.selectedLocaleIds, id] }
      })
    },

    deselectLocale: id => {
      set(state => ({
        selectedLocaleIds: state.selectedLocaleIds.filter(localeId => localeId !== id)
      }))
    },

    toggleLocaleSelection: id => {
      set(state => {
        if (state.selectedLocaleIds.includes(id)) {
          return { selectedLocaleIds: state.selectedLocaleIds.filter(localeId => localeId !== id) }
        }
        return { selectedLocaleIds: [...state.selectedLocaleIds, id] }
      })
    },

    selectAllLocales: locales => {
      set(() => ({
        selectedLocaleIds: locales.map(locale => locale.id)
      }))
    },

    deselectAllLocales: () => {
      set(() => ({ selectedLocaleIds: [] }))
    },

    isLocaleSelected: id => {
      return get().selectedLocaleIds.includes(id)
    },

    // Bulk delete operation
    bulkDeleteLocales: async ids => {
      set(state => ({
        localeState: {
          ...state.localeState,
          isLoading: true,
          error: null
        }
      }))

      try {
        const response = await bulkDeleteLocales({ ids })

        set(state => ({
          localeState: {
            ...state.localeState,
            isLoading: false
          },
          // Only remove successfully deleted items from selection
          selectedLocaleIds: state.selectedLocaleIds.filter(id => !response.deletedItems.includes(id))
        }))

        return response
      } catch (error: any) {
        set(state => ({
          localeState: {
            ...state.localeState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            },
            isLoading: false
          }
        }))

        throw error
      }
    }
  }))
)

export default useLocalesStore
